import React, { useCallback, useMemo, useRef, useState } from 'react';
import Filter from '@/pages/dashboard/components/NewFilter';
import { FORM_MAP } from '@/pages/dashboard/pages/overview/_config/constant';
import { setBtnPointLog } from '@/pages/dashboard/utils/utils';
import { getDateDefault } from '@/pages/dashboard/utils/dateUtils';
import { formatDateFromFilterValue } from '@/pages/dashboard/utils/formatDate';
import { PC_CONSOLE_DEFAULT_FILTER_CONFIG } from '@/pages/dashboard/components/NewFilter/_config';
import { getLocale, shallowEqual, useSelector } from 'umi';
import useEventData from '@/pages/dashboard/servicesModels/overview/overviewEventDataModel';
import {
  buildOverviewEventDataFormValueFromFilterVal,
  toOverviewEventDataOptions,
} from '@/pages/dashboard/servicesModels/overview/overviewEventDataRequestParams';
import { buildPropsEventDataFromEventList } from '@/pages/dashboard/pages/overview/kpiOverview/components/KpiEventMetricTrendService/_utils';
import { groupRawEventListByTypeForModal } from '@/pages/dashboard/components/EventMetricTrend/kpiEventUiUtils';
import { KpiOverviewEventInjectContext } from '@/pages/dashboard/pages/overview/kpiOverview/contexts/KpiOverviewEventInjectContext';
import KpiEventFilter from './KpiEventFilter';
import KpiEventStickySlot from './KpiEventFilter/KpiEventStickySlot';
import { useKpiEventSetting } from './KpiEventFilter/useKpiEventSetting';
import type { IChartEventSetting, IKpiEventFilterLanguage } from './KpiEventFilter';
import {
  KPI_OVERVIEW_EVENT_DATA_SOURCE_DEFAULTS,
  type IKpiEventDataSourceConfig,
} from './kpiOverviewEventDataSourceDefaults';

// 筛选器路径
const FILTER_PATH = 'overview/kpi';

/**
 * KpiOverViewFIlter Props
 */
interface IKpiOverViewFilterProps {
  /** 与 FilterWrap 下发的 filterVal 一致，供事件列表请求 */
  formValue?: Record<string, unknown>;
  /** 与 FilterWrap 一致；为 true 时事件请求等待筛选器初始化完成 */
  filterLoading?: boolean;
  /** 筛选器确认函数 */
  filterConfirm: (value: Record<string, unknown>) => void;
  /** 筛选器重置函数 */
  filterReset: (value: Record<string, unknown>) => void;
  /** 初始化函数，会向外部传递初始化后的筛选器值 */
  filterInit: (value: Record<string, unknown>) => void;
  /** 打点配置 */
  buttonLogConfig?: Record<string, unknown>;
  /** 是否开启吸顶 */
  openSticky?: boolean;
  /** 筛选器路径 */
  path?: string;
  /** 子元素（筛选器下方内容） */
  children?: React.ReactNode;
  /** 是否有筛选器保存功能 */
  isHaveFilterSave?: boolean;
  /** 时区偏移 */
  timeZone?: number;
  /** 自定义类名 */
  className?: string;
  /** 配置项（含 segmentFloatStickySyncId、kpiEventDataSource、kpiEventFilterLanguage 等） */
  config?: {
    segmentFloatStickySyncId?: string;
    kpiEventDataSource?: IKpiEventDataSourceConfig;
    /** 事件筛选区中英文文案，见 KpiEventFilter / kpiEventFilterLanguageDefaults */
    kpiEventFilterLanguage?: Partial<IKpiEventFilterLanguage>;
    [key: string]: unknown;
  };
  // ===== 事件筛选器相关 props =====
  /** 是否显示事件筛选器 */
  showEventFilter?: boolean;
  /** 当前事件设置状态 */
  chartEventSetting?: IChartEventSetting;
  /** 事件设置变更回调 */
  changeEventSetting?: (setting: IChartEventSetting) => void;
  /** 事件弹窗数据覆盖（一般无需传；由本组件根据接口数据计算） */
  eventModalData?: Record<string, unknown[]>;
  /** 时间格式（传给弹窗） */
  eventTimeFormat?: string;
  /** 日期类型（传给弹窗） */
  eventDateType?: 'daily' | 'weekly' | 'monthly' | 'hourly';
  /** X 轴数据（传给弹窗） */
  eventXAxisData?: string[];
}

interface IKpiOverviewGameSlice {
  dashboard?: {
    currentGame?: {
      game_code?: string;
    };
  };
}

// daily筛选器继承逻辑
const KpiOverViewFIlter = (props: IKpiOverViewFilterProps) => {
  const {
    formValue: filterValProp,
    filterLoading = true,
    filterConfirm,
    filterReset,
    filterInit,
    buttonLogConfig,
    openSticky = false,
    path = FILTER_PATH,
    children,
    isHaveFilterSave,
    timeZone = 0,
    className,
    config,
    // 事件筛选器 props
    showEventFilter = true,
    chartEventSetting,
    changeEventSetting: externalChangeSetting,
    eventModalData: eventModalDataProp,
    eventTimeFormat = 'YYYY-MM-DD',
    eventDateType = 'daily',
    eventXAxisData = [],
  } = props;
  const segmentFloatStickySyncId = config?.segmentFloatStickySyncId as
    | string
    | undefined;
  const filterValueRef = useRef<any>(null); // 筛选器值，此处不用state，更新formValue同样会触发组件更新

  const { gameCode } = useSelector(
    (state: IKpiOverviewGameSlice) => {
      return {
        gameCode: state?.dashboard?.currentGame?.game_code,
      };
    },
    shallowEqual,
  );

  const eventSourceCfg = useMemo((): IKpiEventDataSourceConfig => {
    return {
      ...KPI_OVERVIEW_EVENT_DATA_SOURCE_DEFAULTS,
      ...(config?.kpiEventDataSource || {}),
    };
  }, [config?.kpiEventDataSource]);

  const kpiEventFilterLanguage = config?.kpiEventFilterLanguage as
    | Partial<IKpiEventFilterLanguage>
    | undefined;

  const newFormValue = useMemo(
    () =>
      buildOverviewEventDataFormValueFromFilterVal(
        filterValProp as Record<string, unknown> | undefined,
      ),
    [filterValProp],
  );

  const eventDataOptions = useMemo(
    () => toOverviewEventDataOptions(eventSourceCfg),
    [eventSourceCfg],
  );

  const [eventData, eventListLoading, eventListError] = useEventData(
    gameCode as string,
    newFormValue,
    filterLoading,
    eventSourceCfg.api,
    eventDataOptions,
  );

  const eventModalDataFromApi = useMemo(() => {
    const built = buildPropsEventDataFromEventList(eventData);
    return groupRawEventListByTypeForModal(
      built.rawEventList as Array<Record<string, unknown>> | undefined,
    );
  }, [eventData]);

  const eventModalDataMerged = useMemo(
    () => eventModalDataProp ?? eventModalDataFromApi,
    [eventModalDataProp, eventModalDataFromApi],
  );

  // ===== 事件筛选器状态管理（受控/非受控双模式） =====
  const { eventSetting, changeEventSetting } = useKpiEventSetting({
    chartEventSetting,
    changeEventSetting: externalChangeSetting,
    eventModalData: eventModalDataMerged,
  });

  /**
   * Context value 严格分层 memo，避免勾选「显示事件」时其他与事件无关的字段
   * （如 eventData 在轮询/接口返回时的新引用）连带触发下游 KPI 卡 props 变化。
   */
  const kpiSharedEventSetting = useMemo(
    () => ({
      chartEventSetting: eventSetting,
      changeEventSetting,
    }),
    [eventSetting, changeEventSetting],
  );

  const eventInjectValue = useMemo(
    () => ({
      eventData,
      loading: Boolean(eventListLoading),
      kpiSharedEventSetting,
    }),
    [eventData, eventListLoading, kpiSharedEventSetting],
  );

  const stickyTrailingSlot = useMemo(
    () =>
      openSticky && showEventFilter ? (
        <KpiEventStickySlot
          chartEventSetting={eventSetting}
          language={kpiEventFilterLanguage}
        />
      ) : null,
    [openSticky, showEventFilter, eventSetting, kpiEventFilterLanguage, getLocale()],
  );

  const seasonInfo = useSelector(
    (state: any) =>
      state?.dashboardSetting?.gameConfig?.season_info || {
        date: [],
        week: [],
        month: [],
      },
  );
  // 日期默认值（T-1 单日）
  const dateDefault = getDateDefault({
    timeType: 'day',
    timeZone,
    startNum: 1,
    endNum: 1,
  });
  // 格式化后的默认日期，供下游组件对比使用
  const defaultRangeTime = formatDateFromFilterValue({
    rangeTime: dateDefault,
  })?.rangeTime;

  // 设置inputList,和后端核对后，此次筛选器的展示都是由后端控制，配置为时间，地区，平台和语言四个筛选器
  const [inputList] = useState([
    {
      type: 'rangeDaily',
      key: 'rangeTime',
      default: dateDefault,
      extraShortcut: seasonInfo,
    },
  ]);

  // 私有化处理，抹平新旧筛选器的差距
  const onInit = useCallback(
    (value: any, _inputList: any, isHaveExtendFIlter: any) => {
      filterValueRef.current = value;
      // todo 锁定状态下如果锁定时间跟默认时间相同，是否需要单独处理，设置diff为false？
      filterInit({
        ...(formatDateFromFilterValue(value) || {}),
        [FORM_MAP.RANGE_TIME_DIFF]: !isHaveExtendFIlter,
        timeType: 'Day',
        rangeDateKey: 'rangeDaily',
        defaultRangeTime,
      });
    },
    [filterInit, defaultRangeTime],
  );

  const onConfirm = useCallback(
    (value: any) => {
      filterValueRef.current = value;
      const obj: any = {
        ...(formatDateFromFilterValue(value) || {}),
        [FORM_MAP.RANGE_TIME_DIFF]: false,
        timeType: 'Day',
        defaultRangeTime,
      };
      filterConfirm(obj);
      // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
      setBtnPointLog(buttonLogConfig, formatDateFromFilterValue(value));
    },
    [filterConfirm, defaultRangeTime, buttonLogConfig],
  );

  const onReset = useCallback(
    (value: any) => {
      filterValueRef.current = value;
      const obj = {
        ...(formatDateFromFilterValue(value) || {}),
        [FORM_MAP.RANGE_TIME_DIFF]: true,
        timeType: 'Day',
        defaultRangeTime,
      };
      filterReset(obj);
      // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
      setBtnPointLog(buttonLogConfig, formatDateFromFilterValue(value), true);
    },
    [filterReset, defaultRangeTime, buttonLogConfig],
  );

  return (
    <KpiOverviewEventInjectContext.Provider value={eventInjectValue}>
      <div className="kpi-overview-filter-wrapper">
        <Filter
          filterHideDefaultIsReq={true}
          pageDefaultSelector={PC_CONSOLE_DEFAULT_FILTER_CONFIG}
          openSticky={openSticky}
          segmentFloatStickySyncId={segmentFloatStickySyncId}
          className={className}
          path={path}
          latestDateType={''}
          value={filterValueRef.current}
          inputList={inputList}
          onInit={onInit}
          onConfirm={onConfirm}
          onReset={onReset}
          isHaveFilterSave={isHaveFilterSave}
          stickyTrailingSlot={showEventFilter ? stickyTrailingSlot : null}
          tailSlot={
            showEventFilter ? (
              <KpiEventFilter
                chartEventSetting={eventSetting}
                changeEventSetting={changeEventSetting}
                eventModalData={eventModalDataMerged}
                eventModalLoading={eventListLoading}
                eventModalError={eventListError}
                eventTimeFormat={eventTimeFormat}
                eventDateType={eventDateType}
                eventXAxisData={eventXAxisData}
                language={kpiEventFilterLanguage}
              />
            ) : null
          }
        >
          {children}
        </Filter>
      </div>
    </KpiOverviewEventInjectContext.Provider>
  );
};

export default KpiOverViewFIlter;
